import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { Order, OrderCustomer, OrderDiscipline } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { getCustomerUrl, getDisciplineUrl, getKidUrl, getOrderUrl, getSubscriptionPath } from '@shared/lib/routes'
import { formatPrice, useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import {
  ORDER_EMPTY_CELL,
  getOrderPace,
  getOrderPackage,
  useOrderOptions,
} from './order-options'
import { useOrdersStore } from './store'

interface TableOrdersReturn {
  orders: ComputedRef<Order[]>
  isLoading: ComputedRef<boolean>
  hasOrders: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  emptyCell: string
  getTypeLabel: (order: Order) => string
  getInstallment: (order: Order) => string | null
  getCustomerName: (customer: OrderCustomer) => string
  getDisciplineLabel: (discipline: OrderDiscipline) => string
  getPackage: (order: Order) => string
  getPace: (order: Order) => string
  getTotal: (order: Order) => string
  getSucceededAt: (order: Order) => string
  getOrderUrl: (orderId: number) => string
  getSubscriptionPath: (subscriptionId: number) => string
  getCustomerUrl: (customerId: number) => string
  getKidUrl: (kidId: number) => string
  getDisciplineUrl: (disciplineId: number) => string
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableOrders = (): TableOrdersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useOrdersStore()
  const dayjs = useDayjs()
  const { getTypeLabel: getTypeLabelByKey, getDisciplineLabel: getDisciplineLabelByCode } = useOrderOptions()

  const orders = computed<Order[]>(() => store.getOrders)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasOrders = computed<boolean>(() => store.hasOrders)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const getTypeLabel = (order: Order): string => getTypeLabelByKey(order.type)

  /** Installment position within the subscription, e.g. `2/6`. */
  const getInstallment = (order: Order): string | null =>
    order.subscription ? `${order.number ?? ORDER_EMPTY_CELL}/${order.subscription.parts}` : null

  const getCustomerName = (customer: OrderCustomer): string =>
    `${customer.first_name} ${customer.last_name}`.trim()

  const getDisciplineLabel = (discipline: OrderDiscipline): string => getDisciplineLabelByCode(discipline.code)

  const getTotal = (order: Order): string => formatPrice(order.total)

  const getSucceededAt = (order: Order): string =>
    order.succeeded_at ? dayjs(order.succeeded_at).format(DateFormat.KanbanDate) : ORDER_EMPTY_CELL

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    orders,
    isLoading,
    hasOrders,
    meta,
    perPageOptions,
    emptyCell: ORDER_EMPTY_CELL,
    getTypeLabel,
    getInstallment,
    getCustomerName,
    getDisciplineLabel,
    getPackage: getOrderPackage,
    getPace: getOrderPace,
    getTotal,
    getSucceededAt,
    getOrderUrl,
    getSubscriptionPath,
    getCustomerUrl,
    getKidUrl,
    getDisciplineUrl,
    setChangePage,
    setChangePerPage,
  }
}
